import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { RightSidebarOptions, SiteForm, SiteSettingsOptions } from './create-site.inteface';
import { BaseService } from '../../../../../services/base.service';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations, SUCCESS_RESPONSE } from '../../../../../enum/constants';
import { SiteService } from '../../../../../services/site.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { IError } from '../../../../../interfaces';

@Component({
  selector: 'app-create-site',
  templateUrl: './create-site.component.html',
  styleUrls: ['./create-site.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateSiteComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<null>();
  public siteForm!: FormGroup<SiteForm>;
  public isUpdate = false;
  private data!: RightSidebarOptions;
  private settings!: SiteSettingsOptions;
  private site!: any;
  public isLoader: boolean = false;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    private readonly siteService: SiteService,
    public readonly baseService: BaseService,
    private readonly notificationService: NotificationService,
  ) {}

  public ngOnInit() {
    this.data = this.payload;
    this.isUpdate = this.data?.is_update || false;
    if (this.isUpdate) {
      this.isLoader = true;
      this.initSiteSettings();
    } else {
      this.initForm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initForm() {
    this.siteForm = new FormGroup<SiteForm>({
      name: new FormControl(this.isUpdate ? this.site?.name : '', Validators.required),
      subdomain: new FormControl(this.isUpdate ? this.settings?.subdomain : '', [
        Validators.required,
        Validators.minLength(3),
      ]),
      product: new FormControl(this.isUpdate ? this.site?.x_data.product : ''),
      price: new FormControl(this.isUpdate ? this.site?.x_data.price : ''),
      is_product: new FormControl(this.isUpdate ? this.site?.x_data.is_product : false),
    });
  }

  private initSiteSettings() {
    this.siteService.loadSettings(this.data.id).subscribe((settings: SiteSettingsOptions) => {
      this.settings = settings;
      this.initSite();
    });
  }

  private initSite() {
    this.siteService.getSite(this.data.id).subscribe((site: any) => {
      this.site = site;
      this.initForm();
      this.isLoader = false;
    });
  }

  public onSubmit(): void {
    if (this.siteForm.invalid) {
      this.siteForm.markAllAsTouched();
      return;
    }

    this.isUpdate ? this.onUpdate() : this.onCreate();
  }

  public onCreate(): void {
    const form = this.siteForm.getRawValue();
    const payload = {
      ...form,
      x_data: {
        product: form.product,
        price: form.price,
        is_product: form.is_product,
      },
    };
    this.siteService
      .createSite(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            activeTranslations.notifications_texts.success.success,
            activeTranslations.notifications_texts.success.successfully_created,
          );
          this.close.emit('close');
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
            );
            this.close.emit('close');
          });
        },
      });
  }

  public onUpdate(): void {
    const site = {
      name: this.siteForm.controls.name.getRawValue(),
      x_data: {
        product: this.siteForm.controls.product.getRawValue(),
        price: this.siteForm.controls.price.getRawValue(),
        is_product: this.siteForm.controls.is_product.getRawValue(),
      },
    };
    const siteSettings = {
      subdomain: this.siteForm.controls.subdomain.getRawValue(),
    };
    forkJoin([
      this.siteService.updateSite(this.data.id, site),
      this.siteService.saveSettings(this.data.id, siteSettings),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            activeTranslations.notifications_texts.success.success,
            activeTranslations.notifications_texts.success.successfully_updated,
          );
          this.close.emit('close');
        },
        error: (e: any) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError(
              activeTranslations.notifications_texts.error.error,
              ERROR_MESSAGES[err.code] || DEFAULT_ERROR,
            );
          });
          this.close.emit('close');
        },
      });
  }

  onCheckboxChange(isChecked: boolean): void {
    if (isChecked) {
      this.siteForm.controls.is_product.setValue(true);
      this.siteForm.controls.product.setValidators([Validators.required]);
      this.siteForm.controls.price.setValidators([Validators.required]);
    } else {
      this.siteForm.controls.is_product.setValue(false);
      this.siteForm.controls.product.clearValidators();
      this.siteForm.controls.price.clearValidators();
    }
    this.siteForm.updateValueAndValidity();
  }

  public onNoClick(): void {
    this.close.emit('close');
  }
}
